/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, DismissIconButton, Label, NativeInput, Text } from '@/shared/ui';
import {
  clampAdBudgetReais,
  clampAdDurationDays,
  isBelowMinimumAdBudget,
  MAX_AD_DURATION_DAYS,
  minAdDailyBudgetReais,
  MIN_AD_DURATION_DAYS,
} from '@/shared/utils/ad-dialog-helpers';
import { MetaAdsTermsLinks } from '@/features/property-management/components/ad-dialogs/MetaAdsTermsLinks';
import { LEAD_AD_INPUT_CLASSES, LeadAdContentFields, type LeadAdContentValues } from './LeadAdContentFields';

/** One network connection whose Lead Ad (and Boost) defaults can be edited in the modal. */
export interface LeadAdConnectionConfig {
  key: 'facebook' | 'instagram';
  icon: React.ReactNode;
  iconBg: string;
  /** Connection identity shown as the block heading, e.g. "Facebook — Minha Imobiliária". */
  label: string;
  budgetCents: number;
  duration: number;
  /** i18n key group -- 'defaultLeadAd' (Facebook) or 'defaultLeadAdInstagram' (Instagram). */
  titlePrefix: 'defaultLeadAd' | 'defaultLeadAdInstagram';
  /** Distinguishes data-testid attributes between the Facebook and Instagram blocks. */
  testIdPrefix: 'facebook' | 'instagram';
  onSave: (budgetCents: number, duration: number) => Promise<void>;
}

/** Props for the modal that configures Lead Ad defaults per connection. */
export interface LeadAdDefaultsModalProps {
  open: boolean;
  onClose: () => void;
  /** Every connection Lead Ad is currently available for -- the modal lists all of them, never just one. */
  connections: LeadAdConnectionConfig[];
  /** The content/targeting options shared by every connection, saved with the same single Save. */
  content: { values: LeadAdContentValues; onSave: (values: LeadAdContentValues) => Promise<void> };
  t: (key: string, opts?: Record<string, string | number>) => string;
}

type DraftValues = { budgetCents: number; duration: number };

/**
 * Lets an agency set the default daily budget + run duration used both when
 * boosting a post and when auto-creating a Lead Ad on publish, for every
 * network connection Lead Ad is currently available for. Opened from the
 * "Configurar Lead Ads" button at the end of the Facebook & Instagram card
 * -- one shared modal instead of a block duplicated per connection row.
 *
 * A single "Save" action commits every edited connection's current draft at
 * once and closes the modal -- there is no per-connection save button, so
 * the agency never has to reconcile several independent save affordances
 * for what is conceptually one settings form.
 *
 * @param props - See {@link LeadAdDefaultsModalProps}.
 * @returns The modal element, or null when closed.
 */
export function LeadAdDefaultsModal({ open, onClose, connections, content, t }: LeadAdDefaultsModalProps) {
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [contentDirty, setContentDirty] = useState(false);
  const [contentValid, setContentValid] = useState(true);
  // Connections whose budget/duration draft cannot be saved (one filled, the other not, or out of range).
  const [invalidKeys, setInvalidKeys] = useState<Set<string>>(new Set());
  const contentDraftRef = useRef<LeadAdContentValues>(content.values);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const draftsRef = useRef<Map<string, DraftValues>>(new Map());
  const { t: tCommon } = useTranslation('common');

  const handleDirtyChange = useCallback((key: string, dirty: boolean) => {
    setDirtyKeys((prev) => {
      const next = new Set(prev);
      if (dirty) next.add(key); else next.delete(key);
      return next;
    });
  }, []);

  const handleValidityChange = useCallback((key: string, valid: boolean) => {
    setInvalidKeys((prev) => {
      if (prev.has(key) === !valid) return prev;
      const next = new Set(prev);
      if (valid) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleValuesChange = useCallback((key: string, values: DraftValues) => {
    draftsRef.current.set(key, values);
  }, []);

  const handleContentDraft = useCallback(
    (draft: LeadAdContentValues, state: { dirty: boolean; valid: boolean }) => {
      contentDraftRef.current = draft;
      setContentDirty(state.dirty);
      setContentValid(state.valid);
    },
    [],
  );

  if (!open) return null;

  const hasChanges = dirtyKeys.size > 0 || contentDirty;

  const requestClose = () => {
    if (hasChanges) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  };

  const discardAndClose = () => {
    setShowDiscardConfirm(false);
    setDirtyKeys(new Set());
    setContentDirty(false);
    onClose();
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await Promise.all(
        connections
          .filter((c) => dirtyKeys.has(c.key))
          .map((c) => {
            const draft = draftsRef.current.get(c.key);
            return draft ? c.onSave(draft.budgetCents, draft.duration) : Promise.resolve();
          })
          .concat(contentDirty ? [content.onSave(contentDraftRef.current)] : []),
      );
      setDirtyKeys(new Set());
      setContentDirty(false);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1100] flex items-center justify-center" onClick={requestClose}>
      <div className="absolute inset-0 bg-[var(--surface-inverse)]/40" />
      <div
        className="relative w-full max-w-lg mx-(--space-4) rounded-(--radius-xl) bg-(--surface-primary) border border-(--border-subtle) shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-(--space-6) py-(--space-4) border-b border-(--border-subtle)">
          <Text className="m-0 text-[length:var(--text-app-section)] [font-weight:var(--font-semibold)] leading-[var(--leading-tight)] tracking-[var(--tracking-tight)] text-(--text-primary)">
            {t('facebook.leadAdSection.modalTitle')}
          </Text>
          <DismissIconButton label={tCommon('a11y.close')} onClick={requestClose} />
        </div>

        {showDiscardConfirm ? (
          <div className="flex flex-col gap-(--space-5) px-(--space-6) py-(--space-5)">
            <Text className="m-0 text-[length:var(--text-body-sm)] leading-[var(--leading-normal)] text-(--text-primary)">{t('facebook.leadAdSection.discardConfirmMessage')}</Text>
            <div className="flex justify-end gap-(--space-2)">
              <Button
                type="button"
                variant="outlined"
                size="sm"
                onClick={() => setShowDiscardConfirm(false)}
                data-testid="lead-ad-defaults-keep-editing-btn"
              >
                {t('facebook.leadAdSection.keepEditingButton')}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={discardAndClose}
                data-testid="lead-ad-defaults-discard-btn"
                className="bg-[var(--status-error)] hover:bg-[var(--status-error)] hover:opacity-90 text-[var(--text-inverse)]"
              >
                {t('facebook.leadAdSection.discardConfirmButton')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-(--space-4) px-(--space-6) py-(--space-5) max-h-[70vh] overflow-y-auto">
              <Text className="m-0 text-[length:var(--text-body-sm)] leading-[var(--leading-normal)] text-(--text-secondary)">
                {t('facebook.leadAdSection.modalDescription')}
              </Text>

              {connections.map((connection) => (
                <div
                  key={connection.key}
                  data-testid={`lead-ad-defaults-connection-${connection.testIdPrefix}`}
                  className="rounded-(--radius-lg) border border-(--border-subtle) p-(--space-4)"
                >
                  <div className="flex items-center gap-(--space-2-5) mb-(--space-4)">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: connection.iconBg }}
                    >
                      {connection.icon}
                    </div>
                    <Text component="span" className="text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-(--text-primary)">
                      {connection.label}
                    </Text>
                  </div>
                  <ConnectionBudgetFields
                    connection={connection}
                    onDirtyChange={handleDirtyChange}
                    onValidityChange={handleValidityChange}
                    onValuesChange={handleValuesChange}
                    t={t}
                  />
                </div>
              ))}

              <LeadAdContentFields values={content.values} onDraftChange={handleContentDraft} t={t} />

              <div className="flex flex-col gap-(--space-1-5)" data-testid="lead-ad-defaults-authorization-notice">
                <Text className="m-0 text-xs leading-relaxed text-(--text-tertiary)">{t('facebook.adsAuthorizationNotice')}</Text>
                <MetaAdsTermsLinks />
              </div>
            </div>

            <div className="px-(--space-6) py-(--space-4) border-t border-(--border-subtle) flex justify-end">
              <Button
                type="button"
                size="sm"
                onClick={handleSaveAll}
                disabled={saving || !contentValid || invalidKeys.size > 0}
                data-testid="lead-ad-defaults-save-btn"
              >
                {t('facebook.leadAdSection.saveButton')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

const INPUT_CLASSES = LEAD_AD_INPUT_CLASSES;

/**
 * Budget/duration inputs for a single connection inside the modal. Owns only
 * the in-progress draft (local state, synced back to the committed props
 * whenever they change) -- saving itself is the modal's single global
 * action, not this component's concern.
 */
const ConnectionBudgetFields: React.FC<{
  connection: LeadAdConnectionConfig;
  onDirtyChange: (key: string, dirty: boolean) => void;
  onValidityChange: (key: string, valid: boolean) => void;
  onValuesChange: (key: string, values: DraftValues) => void;
  t: (key: string, opts?: Record<string, string | number>) => string;
}> = ({ connection, onDirtyChange, onValidityChange, onValuesChange, t }) => {
  const { key, budgetCents, duration, titlePrefix, testIdPrefix } = connection;
  const [localBudget, setLocalBudget] = useState(Math.round(budgetCents / 100));
  const [localDuration, setLocalDuration] = useState(duration);

  useEffect(() => {
    setLocalBudget(Math.round(budgetCents / 100));
    setLocalDuration(duration);
  }, [budgetCents, duration]);

  const dirty = localBudget !== Math.round(budgetCents / 100) || localDuration !== duration;
  const normalizedBudget = clampAdBudgetReais(localBudget);
  const normalizedDuration = clampAdDurationDays(localDuration);
  // Housien picks no amount: both start empty and the agency fills in its own. A draft
  // is only saveable with both set and in range (an untouched, still-empty one is not saved at all).
  const filled = localBudget > 0 && localDuration > 0;
  const valid = !dirty || (filled && !isBelowMinimumAdBudget(localBudget) && clampAdDurationDays(localDuration) === localDuration);

  useEffect(() => {
    onValidityChange(key, valid);
    return () => onValidityChange(key, true);
  }, [key, valid, onValidityChange]);

  useEffect(() => {
    onDirtyChange(key, dirty);
    return () => onDirtyChange(key, false);
  }, [key, dirty, onDirtyChange]);

  useEffect(() => {
    onValuesChange(key, { budgetCents: normalizedBudget * 100, duration: normalizedDuration });
  }, [key, normalizedBudget, normalizedDuration, onValuesChange]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-(--space-4)">
      <Label className="flex flex-col gap-(--space-1-5)">
        <Text component="span" className="text-xs [font-weight:var(--font-medium)] text-(--text-secondary)">{t(`facebook.${titlePrefix}.budgetLabel`)}</Text>
        <NativeInput
          type="number"
          min={minAdDailyBudgetReais()}
          value={localBudget || ''}
          onChange={(e) => setLocalBudget(Number(e.target.value))}
          onBlur={() => { if (localBudget) setLocalBudget(clampAdBudgetReais(localBudget)); }}
          className={INPUT_CLASSES}
          data-testid={`lead-ad-budget-input-${testIdPrefix}`}
        />
        <Text component="span" className="text-xs leading-relaxed text-(--text-tertiary)">{t(`facebook.${titlePrefix}.budgetHint`)}</Text>
        {localBudget === 0 && (
          <Text component="span" className="text-xs text-(--status-warning-text)" data-testid={`lead-ad-budget-required-${testIdPrefix}`}>
            {t('facebook.leadAdSection.requiredField')}
          </Text>
        )}
        {localBudget > 0 && isBelowMinimumAdBudget(localBudget) && (
          <Text component="span" className="text-xs text-(--status-warning-text)" data-testid={`lead-ad-budget-min-warning-${testIdPrefix}`}>
            {t('facebook.leadAdBudgetMin', { min: minAdDailyBudgetReais() })}
          </Text>
        )}
      </Label>
      <Label className="flex flex-col gap-(--space-1-5)">
        <Text component="span" className="text-xs [font-weight:var(--font-medium)] text-(--text-secondary)">{t(`facebook.${titlePrefix}.durationLabel`)}</Text>
        <NativeInput
          type="number"
          min={MIN_AD_DURATION_DAYS}
          max={MAX_AD_DURATION_DAYS}
          value={localDuration || ''}
          onChange={(e) => setLocalDuration(Number(e.target.value))}
          onBlur={() => { if (localDuration) setLocalDuration(clampAdDurationDays(localDuration)); }}
          className={INPUT_CLASSES}
          data-testid={`lead-ad-duration-input-${testIdPrefix}`}
        />
        <Text component="span" className="text-xs leading-relaxed text-(--text-tertiary)">{t(`facebook.${titlePrefix}.durationHint`)}</Text>
        {localDuration === 0 && (
          <Text component="span" className="text-xs text-(--status-warning-text)" data-testid={`lead-ad-duration-required-${testIdPrefix}`}>
            {t('facebook.leadAdSection.requiredField')}
          </Text>
        )}
      </Label>
    </div>
  );
};
